import { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { OnboardingDocumentMessage } from '../../../types/OnboardingDocumentMessage';
import { Button } from '../../cdl/Button/Button';
import { Modal } from '../../cdl/Modal/Modal';
import { Text } from '../../cdl/Text/Text';
import { uploadFile as uploadCompanyFile } from '../../common/api/clients/fileManagement.api';
import { DropZone } from '../../common/Attachments/components/DropZone';
import { attachmentFileFormats } from '../../common/Attachments/utils/attachmentFileFormats';
import { translate } from '../../common/helpers/translate.helper';
import { IconUpload } from '../../common/icons/cdl/Upload';
import { Spinner } from '../../common/Spinner/Spinner';
import { Box } from '../../common/ui/Box';

import { DocumentRow } from './DocumentRow';

const MAX_FILE_SIZE = 5e7; // 50 MB — matches the FileManagementService limit.
const MAX_DOCUMENTS = 20;

interface SupplierOnboardingDocumentsModalProps {
    isOpen: boolean;
    title: string;
    emptyTitle: string;
    emptyDescription: string;
    submitLabel: string;
    // Company the documents are uploaded under (customer for the request, supplier for the upload).
    companyId: string;
    documents: OnboardingDocumentMessage[];
    readOnlyDocuments?: OnboardingDocumentMessage[];
    isPersisting?: boolean;
    isSending: boolean;
    // Persists the given file ids onto the onboarding (without sending).
    onPersist: (fileIds: string[]) => Promise<unknown>;
    onSubmit: () => void;
    onDismiss: () => void;
}

export const SupplierOnboardingDocumentsModal = ({
    isOpen,
    title,
    emptyTitle,
    emptyDescription,
    submitLabel,
    companyId,
    documents,
    readOnlyDocuments = [],
    isPersisting = false,
    isSending,
    onPersist,
    onSubmit,
    onDismiss,
}: SupplierOnboardingDocumentsModalProps) => {
    const [isUploading, setIsUploading] = useState(false);
    const documentCount = readOnlyDocuments.length + documents.length;
    const isBusy = isUploading || isPersisting;

    const dropzone = useDropzone({
        onDrop: async (droppedFiles) => {
            const filesToUpload = droppedFiles.slice(0, MAX_DOCUMENTS - documentCount);
            if (filesToUpload.length === 0) {
                return;
            }
            setIsUploading(true);
            try {
                const uploaded = await Promise.all(
                    filesToUpload.map((file) => uploadCompanyFile(file, companyId, () => {}))
                );
                await onPersist([...documents.map((document) => document.fileId), ...uploaded.map((file) => file.id)]);
            } finally {
                setIsUploading(false);
            }
        },
        accept: attachmentFileFormats,
        maxSize: MAX_FILE_SIZE,
        multiple: true,
        noClick: true,
        disabled: isBusy,
    });

    const removeDocument = (fileId: string) => {
        return onPersist(documents.filter((document) => document.fileId !== fileId).map((document) => document.fileId));
    };

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss}>
            <Box display="grid" gap={16} padding={24} {...dropzone.getRootProps()}>
                <input {...dropzone.getInputProps()} />

                <Text variant="title">{title}</Text>

                {documentCount === 0 && !isUploading ? (
                    <DropZone
                        isDragAccept={dropzone.isDragAccept}
                        isDragReject={dropzone.isDragReject}
                        isDragActive={dropzone.isDragActive}
                    >
                        <Text variant="fieldLabel" color="foreground.default">
                            {emptyTitle}
                        </Text>
                        <Text variant="small" color="foreground.muted">
                            {emptyDescription}
                        </Text>
                    </DropZone>
                ) : (
                    <Box display="grid" gap={4}>
                        {readOnlyDocuments.map((document) => (
                            <DocumentRow key={document.fileId} document={document} />
                        ))}
                        {documents.map((document) => (
                            <DocumentRow
                                key={document.fileId}
                                document={document}
                                onRemove={() => removeDocument(document.fileId)}
                                removeDisabled={isBusy}
                            />
                        ))}
                        {isUploading ? (
                            <Box padding={4}>
                                <Spinner />
                            </Box>
                        ) : null}
                    </Box>
                )}

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Button
                        emphasis="medium"
                        type="button"
                        LeadingIcon={IconUpload}
                        disabled={documentCount >= MAX_DOCUMENTS || isBusy}
                        onClick={(event) => {
                            event.currentTarget.blur();
                            dropzone.open();
                        }}
                    >
                        {translate('supplierOnboarding.modal.uploadDocuments')}
                    </Button>
                    <Button
                        emphasis="high"
                        type="button"
                        onClick={onSubmit}
                        disabled={documents.length === 0 || isBusy}
                        isLoading={isSending}
                    >
                        {submitLabel}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
